import { useEffect, useState } from 'react';

import {
  Button,
  DateRangePicker,
  ExcelIcon,
  Form,
  Input,
  Pagination,
  RefreshIcon,
  Select,
  Text,
  TextButton,
  TypeSelectOption,
  toastService,
} from '@thefarmersfront/kitchen';

import { PageTitle, Table, TableTopField } from '~/components/common';
import { PageWrapper } from '~/components/layout';

import { useDownloadReady, useToggle } from '~/hooks/common';
import { useGetStocksQueryType, usePostStocksDate, usePostStocksDateExcelDownload } from '~/hooks/stock';
import { useLoadingStore } from '~/store';
import { formatDate, getDateRangeInfo, transformToStockSelectOptions as transformToSelectOptions } from '~/utils';
import { getStockDateColumnDefs } from '~/utils/stock';

import { BREAD_CRUMB, PATH, TOAST_MESSAGE } from '~/constants';
import {
  INITIAL_STOCK_PAGE_OFFSET,
  INITIAL_STOCK_PAGE_SIZE,
  STOCK_PAGE_SIZE,
  TypeStockPageSize,
} from '~/constants/stock';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypePostStocksDateParams, TypeStockDate, TypeStockDateFilter } from '~/types/stock.types';

import styles from './StockDatePage.module.scss';

const INITIAL_STOCK_DATE_FILTER: TypeStockDateFilter = {
  keyword: { value: '', text: '' },
  keywordValue: '',
  center: { value: '', text: '' },
  stockType: { value: '', text: '' },
  temperature: { value: '', text: '' },
  pageSize: INITIAL_STOCK_PAGE_SIZE,
  currentPage: INITIAL_STOCK_PAGE_OFFSET,
  manufactureFromDate: undefined,
  manufactureToDate: undefined,
  expiredFromDate: undefined,
  expiredToDate: undefined,
  salesDeadLineFromDate: undefined,
  salesDeadLineToDate: undefined,
};

const StockDatePage = () => {
  const { setIsLoading } = useLoadingStore();
  const [stockDateFilter, setStockDateFilter] = useState<TypeStockDateFilter>(INITIAL_STOCK_DATE_FILTER);

  const { isDownloadReady, searchedFilter, markDownloadReady, checkFilterChange } = useDownloadReady({
    initialFilter: INITIAL_STOCK_DATE_FILTER,
  });

  const [stockDateData, setStockDateData] = useState<TypeStockDate[] | null>(null);
  const [itemTotal, setItemTotal] = useState<number | null>(null);

  const { getStocksQueryType } = useGetStocksQueryType();
  const { postStocksDate } = usePostStocksDate();
  const { postStocksDateExcelDownload } = usePostStocksDateExcelDownload();

  const [stockKeyword, setStockKeyword] = useState<TypeSelectOption<string>[]>([]);
  const [stockCenter, setStockCenter] = useState<TypeSelectOption<string>[]>([]);
  const [stockType, setStockType] = useState<TypeSelectOption<string>[]>([]);
  const [stockTemperature, setStockTemperature] = useState<TypeSelectOption<string>[]>([]);

  const pageOptions = Object.values(STOCK_PAGE_SIZE);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { columnDefs } = getStockDateColumnDefs();

  const fetchStocksQueryType = async () => {
    try {
      setIsLoading(true);

      const { data } = await getStocksQueryType();

      const { keyWord, thirdPartyClusterType, stockType, temperatureType } = data;

      const keywordOptions = transformToSelectOptions(keyWord);
      const centerOptions = transformToSelectOptions(thirdPartyClusterType);
      const typeOptions = transformToSelectOptions(stockType);
      const temperatureOptions = transformToSelectOptions(temperatureType);

      setStockKeyword(keywordOptions);
      setStockCenter(centerOptions);
      setStockType(typeOptions);
      setStockTemperature(temperatureOptions);

      const initialStockDateFilter: TypeStockDateFilter = {
        ...stockDateFilter,
        keyword: keywordOptions?.[0],
        center: centerOptions?.[0],
        stockType: typeOptions?.[0],
        temperature: temperatureOptions?.[0],
      };

      setStockDateFilter(initialStockDateFilter);
      markDownloadReady({ filterValue: initialStockDateFilter, hasData: false });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        persist: true,
        message:
          (error as TypeErrorResponseData['error'])?.message ||
          '재고 필터 정보를 호출하는데 실패했습니다.\n페이지를 새로고침 해주세요.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStockDate = async (stockDateFilter: TypeStockDateFilter) => {
    try {
      setIsLoading(true);

      const {
        keyword,
        keywordValue,
        stockType,
        center,
        temperature,
        pageSize,
        currentPage,
        manufactureFromDate,
        manufactureToDate,
        expiredFromDate,
        expiredToDate,
        salesDeadLineFromDate,
        salesDeadLineToDate,
      } = stockDateFilter;

      const params: TypePostStocksDateParams = {
        keyWord: String(keyword.value),
        keyWordValue: keywordValue?.trim(),
        stockType: String(stockType.value),
        thirdPartyClusterType: String(center.value),
        temperatureType: String(temperature.value),
        limit: pageSize,
        cursor: currentPage,
        manufactureFromDate: formatDate(manufactureFromDate, 'YYYYMMDD'),
        manufactureToDate: formatDate(manufactureToDate, 'YYYYMMDD'),
        expiredFromDate: formatDate(expiredFromDate, 'YYYYMMDD'),
        expiredToDate: formatDate(expiredToDate, 'YYYYMMDD'),
        salesDeadLineFromDate: formatDate(salesDeadLineFromDate, 'YYYYMMDD'),
        salesDeadLineToDate: formatDate(salesDeadLineToDate, 'YYYYMMDD'),
      };

      const { data, pagination } = await postStocksDate(params);

      setStockDateFilter(stockDateFilter);
      setStockDateData(data);
      setItemTotal(pagination.listTotal);

      markDownloadReady({ filterValue: stockDateFilter, hasData: data.length > 0 });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message:
          (error as TypeErrorResponseData['error'])?.message ||
          '재고 정보를 호출하는데 실패했습니다.\n다시 시도해주세요.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchStockDateExcelDownload = async () => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      const params = {
        keyWord: searchedFilter.keyword.value,
        keyWordValue: searchedFilter.keywordValue?.trim(),
        stockType: searchedFilter.stockType.value,
        thirdPartyClusterType: searchedFilter.center.value,
        temperatureType: searchedFilter.temperature.value,
        manufactureFromDate: formatDate(searchedFilter.manufactureFromDate, 'YYYYMMDD'),
        manufactureToDate: formatDate(searchedFilter.manufactureToDate, 'YYYYMMDD'),
        expiredFromDate: formatDate(searchedFilter.expiredFromDate, 'YYYYMMDD'),
        expiredToDate: formatDate(searchedFilter.expiredToDate, 'YYYYMMDD'),
        salesDeadLineFromDate: formatDate(searchedFilter.salesDeadLineFromDate, 'YYYYMMDD'),
        salesDeadLineToDate: formatDate(searchedFilter.salesDeadLineToDate, 'YYYYMMDD'),
      };

      await postStocksDateExcelDownload(params);

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      toastService.removeToast(downloadingToastId);
    }
  };

  const handleSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    fetchStockDate({
      ...stockDateFilter,
      currentPage: INITIAL_STOCK_PAGE_OFFSET,
    });
  };

  const handleClickReset = () => {
    setStockDateFilter({
      ...stockDateFilter,
      keyword: stockKeyword?.[0],
      keywordValue: '',
      center: stockCenter?.[0],
      stockType: stockType?.[0],
      temperature: stockTemperature?.[0],
      manufactureFromDate: undefined,
      manufactureToDate: undefined,
      expiredFromDate: undefined,
      expiredToDate: undefined,
      salesDeadLineFromDate: undefined,
      salesDeadLineToDate: undefined,
    });
  };

  const handleClickExcelDownload = () => {
    fetchStockDateExcelDownload();
  };

  const handleChangeKeyword = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockDateFilter({
      ...stockDateFilter,
      keyword: selectedOption,
      keywordValue: '',
    });
  };

  const handleChangeKeywordValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setStockDateFilter({ ...stockDateFilter, keywordValue: value });
  };

  const handleBlurKeywordValue = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    setStockDateFilter({ ...stockDateFilter, keywordValue: value.trim() });
  };

  const handleChangeCenter = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockDateFilter({
      ...stockDateFilter,
      center: selectedOption,
    });
  };

  const handleChangeStockType = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockDateFilter({
      ...stockDateFilter,
      stockType: selectedOption,
    });
  };

  const handleChangeTemperatureType = (_: unknown, selectedOption: TypeSelectOption<string>) => {
    setStockDateFilter({
      ...stockDateFilter,
      temperature: selectedOption,
    });
  };

  const handleChangeManufactureDate = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start;
    const endDate = selectedDate?.end;

    setStockDateFilter({
      ...stockDateFilter,
      manufactureFromDate: startDate,
      manufactureToDate: endDate,
    });
  };

  const handleChangeExpiredDate = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start;
    const endDate = selectedDate?.end;

    setStockDateFilter({
      ...stockDateFilter,
      expiredFromDate: startDate,
      expiredToDate: endDate,
    });
  };

  const handleChangeSalesDeadLineDate = (selectedDate: { start: Date | null; end: Date | null } | null) => {
    const startDate = selectedDate?.start;
    const endDate = selectedDate?.end;

    setStockDateFilter({
      ...stockDateFilter,
      salesDeadLineFromDate: startDate,
      salesDeadLineToDate: endDate,
    });
  };

  const handleChangeLimit = (_: unknown, { value }: TypeSelectOption) => {
    fetchStockDate({
      ...stockDateFilter,
      pageSize: value as TypeStockPageSize,
      currentPage: 0,
    });
  };

  const handleClickPage = (e: React.MouseEvent<HTMLButtonElement>) => {
    const newPage = Number(e.currentTarget.value);

    fetchStockDate({
      ...stockDateFilter,
      currentPage: newPage - 1,
    });
  };

  useEffect(() => {
    fetchStocksQueryType();
  }, []);

  useEffect(() => {
    checkFilterChange(stockDateFilter);
  }, [stockDateFilter]);

  return (
    <PageWrapper>
      <PageTitle
        title="유통기한별 조회"
        breadcrumbs={BREAD_CRUMB[PATH.STOCK_DATE]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSubmitSearch}>
          <div className={styles['filters-container']}>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                키워드
              </Text>
              <div className={styles['input-container']}>
                <Select
                  className={styles['input-select']}
                  value={stockDateFilter.keyword}
                  options={stockKeyword}
                  onChange={handleChangeKeyword}
                  size="small"
                />
                <Input
                  type="text"
                  value={stockDateFilter.keywordValue}
                  placeholder="입력"
                  size="small"
                  maxLength={200}
                  onChange={handleChangeKeywordValue}
                  onBlur={handleBlurKeywordValue}
                />
              </div>
            </div>
            <div className={styles['filter-column-container']}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  보관 센터
                </Text>
                <Select
                  className={styles['center-select']}
                  value={stockDateFilter.center}
                  options={stockCenter}
                  size="small"
                  onChange={handleChangeCenter}
                />
              </div>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  재고 유형
                </Text>
                <Select
                  className={styles['center-select']}
                  value={stockDateFilter.stockType}
                  options={stockType}
                  size="small"
                  onChange={handleChangeStockType}
                />
              </div>
            </div>
            <div className={styles['filter-column-container']}>
              <div className={styles['filter-container']}>
                <Text className={styles['filter-label']} weight="semi-bold" size="small">
                  보관 유형
                </Text>
                <Select
                  className={styles['center-select']}
                  value={stockDateFilter.temperature}
                  options={stockTemperature}
                  size="small"
                  onChange={handleChangeTemperatureType}
                />
              </div>
            </div>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                제조일
              </Text>
              <DateRangePicker
                allowSingleDateSelect
                size="small"
                placeholder={{
                  start: '날짜 선택',
                  end: '날짜 선택',
                }}
                value={getDateRangeInfo(stockDateFilter?.manufactureFromDate, stockDateFilter?.manufactureToDate)}
                onChange={handleChangeManufactureDate}
              />
            </div>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                소비기한
              </Text>
              <DateRangePicker
                allowSingleDateSelect
                size="small"
                placeholder={{
                  start: '날짜 선택',
                  end: '날짜 선택',
                }}
                value={getDateRangeInfo(stockDateFilter?.expiredFromDate, stockDateFilter?.expiredToDate)}
                onChange={handleChangeExpiredDate}
              />
            </div>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                판매 마감일
              </Text>
              <DateRangePicker
                allowSingleDateSelect
                size="small"
                placeholder={{
                  start: '날짜 선택',
                  end: '날짜 선택',
                }}
                value={getDateRangeInfo(stockDateFilter?.salesDeadLineFromDate, stockDateFilter?.salesDeadLineToDate)}
                onChange={handleChangeSalesDeadLineDate}
              />
            </div>
          </div>
          <div className={styles['filter-actions']}>
            <div className={styles['filter-actions-left-field']} />
            <div className={styles['filter-actions-center-field']}>
              <Button type="submit" size="small">
                검색
              </Button>
            </div>
            <div className={styles['filter-actions-right-field']}>
              <TextButton
                className={styles['filter-reset-button']}
                leftIcon={<RefreshIcon size={16} />}
                size="small"
                color="secondary"
                onClick={handleClickReset}
              >
                필터 초기화
              </TextButton>
            </div>
          </div>
        </Form>
      )}
      <div className={styles['view-section']}>
        <TableTopField
          dataTotalCount={itemTotal ?? 0}
          rightButton={
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              leftIcon={<ExcelIcon size={16} />}
              disabled={!isDownloadReady}
              onClick={handleClickExcelDownload}
            >
              엑셀 다운로드
            </Button>
          }
        />
        <div className={styles['view-section-table-wrapper']}>
          <Table
            className={styles['view-table']}
            data={stockDateData}
            options={{ columnDefs }}
            rowStatusType={stockDateData === null ? 'INIT' : 'EMPTY'}
          />
        </div>
      </div>
      <div className={styles['view-actions-section']}>
        <div className={styles['view-options']}>
          <Select
            className={styles['limit-select']}
            options={pageOptions}
            value={STOCK_PAGE_SIZE[stockDateFilter.pageSize]}
            size="tiny"
            dropdownPlacement="top-left"
            onChange={handleChangeLimit}
          />
        </div>
        {itemTotal ? (
          <Pagination
            className={styles['pagination']}
            currentPage={stockDateFilter.currentPage + 1}
            totalCount={itemTotal ?? 0}
            pageSize={stockDateFilter.pageSize}
            onClick={handleClickPage}
          />
        ) : null}
      </div>
    </PageWrapper>
  );
};

export default StockDatePage;
